import { http } from '@/api/http'
import { toListParams, type ListQuery } from '@/api/query'
import type {
  ListResponse,
  WarehouseDocument,
  WarehouseDocumentHistory,
  WarehouseFreezingSource,
  WarehouseReceivableTruck,
  WarehouseStockOption,
  WarehouseTruckLine,
} from '@/types/api'
import type { RequestOptions } from '../crud'

export type WarehouseLineAmount = { weight: string; count: string; front_count: string; rear_count: string }

export type ReceivingLineInput = WarehouseLineAmount & {
  line_id?: string
  transport_content_id?: string
  product_id?: string
  batch_id?: string
  packaging_id?: string
}

export type ReceivingInput = {
  transport_unit_id?: string
  warehouse_id?: string
  receiver_profile_id?: string
  receiver_employee_id: string
  document_date: string
  is_confirmed: boolean
  comment: string
  lines: ReceivingLineInput[]
}

export type TransferInput = {
  from_warehouse_id: string
  to_warehouse_id: string
  cell_id: string
  driver_profile_id: string
  vehicle_number: string
  document_date: string
  comment: string
  lines: (WarehouseLineAmount & { product_id: string; packaging_id: string })[]
  /** «Проводка»: false keeps a draft that moves no stock. */
  is_confirmed: boolean
}

export type InventoryInput = {
  warehouse_id: string
  receiver_profile_id: string
  document_date: string
  comment: string
  lines: (WarehouseLineAmount & { product_id: string; lot_id: string; packaging_id: string })[]
  /** «Проводка»: false keeps a draft (no stock, no money); switching it needs warehouse_inventory.post. */
  is_confirmed: boolean
}

/**
 * One warehouse document kind. Documents are never deleted: stock moves only
 * through them, and mistakes are corrected by editing (receivings) or by a
 * new document.
 */
function documents<TInput>(path: string, edit: 'put' | 'comment') {
  const base = `/warehouse/${path}`
  return {
    path: `warehouse/${path}`,
    async list(query?: ListQuery, options?: RequestOptions) {
      const { data } = await http.get<ListResponse<WarehouseDocument>>(base, { params: toListParams(query), ...options })
      return { count: data.count ?? 0, items: data.items ?? [] }
    },
    async get(id: string, options?: RequestOptions) {
      const { data } = await http.get<WarehouseDocument>(`${base}/${encodeURIComponent(id)}`, options)
      return data
    },
    async create(body: TInput) {
      const { data } = await http.post<WarehouseDocument>(base, body)
      return data
    },
    async update(id: string, body: Partial<TInput> | { comment: string }) {
      const url = `${base}/${encodeURIComponent(id)}`
      const { data } = edit === 'put' ? await http.put<WarehouseDocument>(url, body) : await http.patch<WarehouseDocument>(`${url}/comment`, body)
      return data
    },
    /** The document's change history, newest first (receivings and transfers). */
    async history(id: string, options?: RequestOptions) {
      const { data } = await http.get<WarehouseDocumentHistory[]>(`${base}/${encodeURIComponent(id)}/history`, options)
      return data ?? []
    },
  }
}

export const warehouseReceivingsService = {
  ...documents<ReceivingInput>('receivings', 'put'),
  async trucks(search = '', options?: RequestOptions) {
    const { data } = await http.get<WarehouseReceivableTruck[]>('/warehouse/receivings/trucks', { params: { search }, ...options })
    return data ?? []
  },
  async truckLines(id: string, options?: RequestOptions) {
    const { data } = await http.get<WarehouseTruckLine[]>(`/warehouse/receivings/trucks/${encodeURIComponent(id)}/lines`, options)
    return data ?? []
  },
}

/** A «Филиал» of the «Приёмка» form: Tashkent (is_head_office) receives trucks, a branch a shipment from Tashkent. */
export type ReceivingBranch = { id: string; code: string; name_en: string; name_ru: string; name_uz: string; name_cyr: string; is_head_office: boolean }

/**
 * «Приёмка»: truck receivings of the head office and branch receivings in
 * one list (each row has kind and branch_name). A branch user gets only its
 * own branch's. Saving goes to each kind's own endpoint.
 */
export const warehouseAllReceivingsService = {
  async list(query?: ListQuery, options?: RequestOptions) {
    const { data } = await http.get<ListResponse<WarehouseDocument>>('/warehouse/all-receivings', { params: toListParams(query), ...options })
    return { count: data.count ?? 0, items: data.items ?? [] }
  },
  async get(id: string, options?: RequestOptions) {
    const { data } = await http.get<WarehouseDocument>(`/warehouse/all-receivings/${encodeURIComponent(id)}`, options)
    return data
  },
  async branches(options?: RequestOptions) {
    const { data } = await http.get<ReceivingBranch[]>('/warehouse/all-receivings/branches', options)
    return data ?? []
  },
}

export const warehouseTransfersService = documents<TransferInput>('transfers', 'put')
/**
 * Inventories: a draft (or one being unposted) is saved whole; a posted one
 * that stays posted changes only its comment.
 */
export const warehouseInventoriesService = {
  ...documents<InventoryInput>('inventories', 'put'),
  async update(id: string, body: InventoryInput | { comment: string }) {
    const url = `/warehouse/inventories/${encodeURIComponent(id)}`
    const { data } = 'lines' in body ? await http.put<WarehouseDocument>(url, body) : await http.patch<WarehouseDocument>(`${url}/comment`, body)
    return data
  },
}

export type FreezingInput = {
  warehouse_id: string
  document_date: string
  comment: string
  lines: (WarehouseLineAmount & { cell_id: string; lot_id: string; product_id: string; target_product_id: string; packaging_id: string })[]
  /** «Проводка»: false keeps a draft that moves no stock; switching it needs warehouse_freezing.post. */
  is_confirmed: boolean
}

/** «Заморозка»: fresh Belarus meat from a truck cell or «/S» row becomes a frozen product; edited like it is created. */
export const warehouseFreezingsService = {
  ...documents<FreezingInput>('freezings', 'put'),
  /** The fresh Belarus meat of a warehouse a freezing may take, with what each source holds now. */
  async sources(warehouseId: string, options?: RequestOptions) {
    const { data } = await http.get<WarehouseFreezingSource[]>('/warehouse/freezings/sources', { params: { warehouse_id: warehouseId }, ...options })
    return data ?? []
  },
}

export async function warehouseStockOptions(
  /** as_of: a back-dated inventory's date — the stock then (RFC 3339). */
  params: { warehouse_id: string; cell_id?: string; transfer_copies?: boolean; search?: string; as_of?: string },
  options?: RequestOptions,
) {
  const { data } = await http.get<WarehouseStockOption[]>('/warehouse/stock-options', { params, ...options })
  return data ?? []
}
